import type { DealFieldDef, RawDealFile } from '@/services/dealFile'
import { readFieldValue } from '@/services/dealFile'
import type { DossierAddress, DossierPropertyMaster } from '@/services/dossierContext'
import { displayedAddress, type ResolvedAddress } from '@/components/deal/dossier/addressPrecedence'
import { parseDateValue } from '@/utils/dates'
import { US_STATES } from '@/utils/geo'
import { millionsFigure } from '@/utils/money'

const DASH = '—'

export function displayValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return String(value)
  return String(value)
}

export function formatAmount(value: unknown): string {
  if (value === null || value === undefined || value === '') return DASH
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return DASH
  // To $10,000, half up: one decimal showed a $17.25M ask as $17.3M.
  if (Math.abs(n) >= 1_000_000) return `$${millionsFigure(n)}M`
  // Money has cents: an ADR of 248.5 is $248.50, not "$248.5".
  if (!Number.isInteger(n)) return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  return `$${n.toLocaleString()}`
}

export function formatMoney(value: unknown, fallback = DASH): string {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n === 0) return fallback
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 2)}M`
  return `$${Math.round(n).toLocaleString()}`
}

export function formatPct(value: unknown, fallback = DASH): string {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n === 0) return fallback
  // Magnitude, not sign: `n <= 1.5` scaled every negative percentage by a
  // hundred, so a stored -650.5 rendered as -65050.43%.
  const scaled = Math.abs(n) <= 1.5 ? n * 100 : n
  return `${scaled.toFixed(scaled % 1 === 0 ? 0 : 2)}%`
}

/**
 * A catalogue percent held in `custom_fields`, which is always on the 0-100
 * scale (the parser's `pct100` and the derivation rules both write points).
 *
 * `formatPct` guesses the scale from the magnitude and reads anything up to
 * 1.5 as a fraction — right for the native `ltv` column, wrong here: a stated
 * 1.00% SOFR floor printed as "100%", a 0.50% floor as "50%", and a 1.2%
 * in-place debt yield as "120%".
 */
export function formatPoints(value: unknown, fallback = DASH): string {
  if (value === null || value === undefined || value === '') return fallback
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[%,\s]/g, ''))
  if (!Number.isFinite(n) || n === 0) return fallback
  return `${n.toFixed(n % 1 === 0 ? 0 : 2)}%`
}

/**
 * Catalogue percents that are real Loan columns, and so keep a FRACTION
 * (`loans.ltv` holds 0.7). Every other catalogue percent rides in
 * `custom_fields` on the 0-100 scale. Mirrors `NATIVE_PERCENT_FIELDS` in
 * backend/app/services/deal_values.py — `ltv` is the only one today.
 */
export const NATIVE_FRACTION_PERCENTS: ReadonlySet<string> = new Set(['ltv'])

/**
 * A named catalogue percent, on the scale its column keeps: the native ltv
 * column holds a fraction (the magnitude rule `formatPct` applies), every
 * other catalogue percent holds points. A stated 0.50% floor read "50%"
 * wherever the magnitude guess met it.
 */
export function formatPercentField(value: unknown, name: string, fallback = DASH): string {
  return NATIVE_FRACTION_PERCENTS.has(name) ? formatPct(value, fallback) : formatPoints(value, fallback)
}

/**
 * One figure of a derivation variance (a stated value the deal's other
 * figures do not reproduce), from the save bar's preview, a field's lineage or
 * the intake dossier.
 *
 * The server states every percent here on the 0-100 points scale —
 * `derivation_values` lifts the native ltv column onto it before the rules
 * run — so a percent is printed as points, never through the magnitude guess:
 * Calloway's computed cash-on-cash of 0.739 read "73.88%" on the save bar and
 * as the raw float 0.7387755102040816 on the tile.
 */
export function formatVarianceFigure(value: unknown, kind: string | undefined): string {
  if (value === null || value === undefined || value === '') return DASH
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[%,\s]/g, ''))
  if (!Number.isFinite(n)) return String(value)
  if (kind === 'percent') return `${n.toFixed(Number.isInteger(n) ? 0 : 2)}%`
  if (kind === 'currency') return formatFieldValue(n, 'currency')
  return formatFieldValue(n, 'number')
}

/**
 * A number with the catalogue unit it belongs to.
 *
 * A workbook cell holds a rate as the fraction Excel stores (0.0786), and a
 * percent field reads it as the percentage it is — the same magnitude rule the
 * percent formatter applies everywhere else. Appending the unit to the raw
 * number printed a 7.86% cap rate as "0.079 %".
 */
export function formatUnitValue(value: number, unit: string | null | undefined): string {
  const u = (unit ?? '').trim()
  if (u === '%') return formatFieldValue(value, 'percent')
  if (u.toLowerCase() === 'x') return `${Number(value.toFixed(2))}x`
  const grouped = value.toLocaleString()
  return u && u !== 'USD' ? `${grouped} ${u}` : grouped
}

export function formatMultiple(value: unknown, fallback = DASH): string {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n === 0) return fallback
  return `${n.toFixed(n % 1 === 0 ? 0 : 2)}x`
}

export function formatDate(value: unknown, fallback = DASH): string {
  if (!value) return fallback
  // A date-only value is the calendar day it names, in the reader's zone.
  const d = parseDateValue(value)
  if (!d) return String(value)
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
}

export function asNumber(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) && n !== 0 ? n : undefined
}

export function catalogueValue(raw: RawDealFile | undefined, defs: DealFieldDef[], name: string): unknown {
  if (!raw) return undefined
  const def = defs.find((d) => d.name === name)
  if (def) return readFieldValue(raw, def)
  if (raw[name] !== undefined && raw[name] !== null && raw[name] !== '') return raw[name]
  const custom = raw.custom_fields
  if (custom && typeof custom === 'object') {
    const v = (custom as Record<string, unknown>)[name]
    if (v !== undefined && v !== null && v !== '') return v
  }
  return undefined
}

export function textOrDash(value: unknown, fallback = DASH): string {
  if (value === null || value === undefined || value === '' || value === DASH) return fallback
  return String(value)
}

/** A form label bound in after the state — "Zip Code", "Postal Code", or the
 *  bare "Code" — is not part of the address ("…, Washington, DC Code"). Only
 *  a label AFTER a state is one; "12 Code Street" is a street. Mirrors
 *  `strip_address_labels` in backend/app/parsing/location.py, which cleans
 *  new captures; this keeps a value stored before that fix off the header. */
const STATE_NAMES = Object.values(US_STATES)
  .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  .sort((a, b) => b.length - a.length)
  .join('|')
const ADDRESS_LABEL_AFTER_STATE = new RegExp(
  `(\\b[A-Z]{2}|\\b(?:${STATE_NAMES}))((?:[\\s,;|]+(?:[Zz]ip(?:\\s*\\+\\s*4)?(?:\\s*[Cc]ode)?|ZIP(?:\\s*CODE)?|[Pp]ostal(?:\\s*[Cc]ode)?|POSTAL(?:\\s*CODE)?|[Cc]ode|CODE)\\b\\.?\\s*[:#]?)+)(\\s*\\d{5}(?:-\\d{4})?)?\\s*$`,
)

export function stripAddressLabels(value: string): string {
  return value.replace(ADDRESS_LABEL_AFTER_STATE, (_match, state: string, _labels: string, zip?: string) =>
    zip ? `${state} ${zip.trim()}` : state,
  )
}

/** The street / city / state / ZIP the deal file itself states, and its
 *  `location` text — each cleaned of the form labels a parser can read in. */
export function dealAddressParts(
  raw: RawDealFile | undefined,
  defs: DealFieldDef[],
): { street: string; city: string; state: string; zip: string; location: string } {
  if (!raw) return { street: '', city: '', state: '', zip: '', location: '' }
  const zipText = textOrDash(catalogueValue(raw, defs, 'zip'), '')
  return {
    street: textOrDash(catalogueValue(raw, defs, 'street'), ''),
    city: textOrDash(catalogueValue(raw, defs, 'city'), ''),
    state: stripAddressLabels(textOrDash(catalogueValue(raw, defs, 'state'), '')),
    // A ZIP holds digits; a label word read into it is not one.
    zip: /\d/.test(zipText) ? zipText : '',
    location: stripAddressLabels(textOrDash(catalogueValue(raw, defs, 'location'), '')),
  }
}

/**
 * The address the deal shows, by the one order every surface uses
 * (`addressPrecedence.ts`): person-entered, package-stated, then the property
 * master. A part a Places name lookup wrote is withheld, not shown. Parts that
 * name neither a street nor a city say less than the stated location — a
 * portfolio across several cities keeps only its state.
 *
 * `context` is the dossier context when it has loaded. Without it only the
 * deal file speaks, which is what the header shows while the context loads.
 */
export function resolveDealAddress(
  raw: RawDealFile | undefined,
  defs: DealFieldDef[],
  context?: { property?: DossierPropertyMaster | null; address?: DossierAddress | null } | null,
): ResolvedAddress | null {
  if (!raw) return null
  const parts = dealAddressParts(raw, defs)
  return displayedAddress({
    deal: parts,
    location: parts.location,
    master: context?.property ?? null,
    server: context?.address ?? null,
  })
}

export function composeAddress(
  raw: RawDealFile | undefined,
  defs: DealFieldDef[],
  context?: { property?: DossierPropertyMaster | null; address?: DossierAddress | null } | null,
): string {
  return resolveDealAddress(raw, defs, context)?.full ?? ''
}

const WEBSITE_FIELDS = ['website', 'property_website', 'website_url', 'property_url'] as const

export function readWebsiteUrl(raw: RawDealFile | undefined, defs: DealFieldDef[]): string {
  if (!raw) return ''
  for (const name of WEBSITE_FIELDS) {
    const value = catalogueValue(raw, defs, name)
    const text = typeof value === 'string' ? value.trim() : ''
    if (isHttpUrl(text)) return text
  }
  return ''
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export function isPresent(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false
  if (typeof value === 'number') return Number.isFinite(value)
  return String(value).trim() !== '' && String(value) !== '—'
}

/** Catalogue fields that hold a calendar year. The catalogue types them as
 *  numbers, and a number is grouped — so Year Built read "2,011". */
const YEAR_FIELDS = new Set(['year_built', 'year_renovated', 'exit_year'])

export function isYearField(name: string | null | undefined): boolean {
  return name != null && YEAR_FIELDS.has(name)
}

/**
 * A catalogue value as a reader expects to see it, by the kind of thing it is.
 *
 * `displayValue` returns the raw string, which is right for an input a person
 * is typing into and wrong everywhere else: a $36,960,000 loan read as
 * "36960000", a 70% LTV as "70", and a 240-unit count with no separator. The
 * catalogue used to live on one tab; it now renders on four, so the difference
 * is on most of the dossier.
 *
 * Money and percentages carry their symbol; everything else numeric gets
 * thousands separators. Fractional precision is preserved rather than rounded
 * away — a 1.28x DSCR and a 5.75% exit cap both matter to two places.
 */
export function formatFieldValue(value: unknown, kind: string | undefined, name?: string): string {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''))
  if (!Number.isFinite(n) || String(value).trim() === '') return String(value)
  // A year is written as a year, never with a thousands separator.
  if (isYearField(name) && Number.isInteger(n)) return String(n)

  const decimals = (() => {
    const text = String(n)
    const dot = text.indexOf('.')
    return dot === -1 ? 0 : Math.min(text.length - dot - 1, 4)
  })()
  const grouped = n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

  switch (kind) {
    case 'currency': {
      // At most two places. A derived per-unit price carried four and read as
      // "$220,833.3333"; money has cents and nothing finer.
      const places = Number.isInteger(n) ? 0 : 2
      const money = Math.abs(n).toLocaleString(undefined, {
        minimumFractionDigits: places, maximumFractionDigits: places,
      })
      // Negative amounts are outflows and read as such, not as "$-12,000".
      return n < 0 ? `-$${money}` : `$${money}`
    }
    case 'percent': {
      // Percent values reach the catalogue on two scales, and both are correct
      // where they are stored: a native loan column keeps a fraction (the PDF
      // layer's `fmt_pct` multiplies by 100 unconditionally), while a value
      // routed into `custom_fields` has already crossed `pct100`. So `ltc`
      // arrives as 66.25 and `ltv` as 0.7, and rendering both literally showed
      // a 70% loan as "0.7%".
      //
      // A NAMED catalogue field is read on the scale its column keeps: only a
      // native percent column holds a fraction. Guessing from the magnitude
      // read a stated 0.50% SOFR floor as "50%". A value with no field name (a
      // workbook cell, which Excel stores as a fraction) keeps the magnitude
      // rule `formatPct` applies. Display only: editing still shows the stored
      // number, so a round-trip cannot change the scale of what is saved.
      if (name && !NATIVE_FRACTION_PERCENTS.has(name)) {
        return `${n.toLocaleString(undefined, {
          minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
          maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
        })}%`
      }
      const scaled = Math.abs(n) <= 1.5 && n !== 0 ? n * 100 : n
      const places = Number.isInteger(scaled) ? 0 : Math.min(2, decimals || 2)
      return `${scaled.toLocaleString(undefined, {
        minimumFractionDigits: places, maximumFractionDigits: places,
      })}%`
    }
    case 'number':
      return grouped
    default:
      return String(value)
  }
}
